import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Users, Globe } from "lucide-react";

const VALUES = [
    {
        icon: Target,
        title: "Service-first",
        desc: "We don't sell software. We solve logistics. Every interaction is human-driven and accountable.",
    },
    {
        icon: Users,
        title: "Trust over self-service",
        desc: "Our customers don't manage dashboards. They trust us. That trust is built one load at a time.",
    },
    {
        icon: Globe,
        title: "Network advantage",
        desc: "A vetted carrier network spanning regions and equipment types — built for reliability at scale.",
    },
];

const About = () => {
    return (
        <div data-testid="about-page" className="relative">
            <section className="relative pt-40 pb-20 md:pt-48 md:pb-28">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative mx-auto max-w-5xl px-5 md:px-8">
                    <p className="mono text-xs text-brand-green uppercase mb-5">
                        / About Nexship
                    </p>
                    <h1 className="display text-5xl md:text-7xl text-[#1a1a1a]">
                        Logistics, <br />
                        <span className="text-brand-green glow-text">
                            handled.
                        </span>
                    </h1>
                    <p className="mt-8 text-gray-700 text-lg max-w-2xl leading-relaxed">
                        Nexship is a transport brokerage. We sit between
                        customers who need goods moved and carriers who can
                        move them. Unlike marketplaces or apps, we manage the
                        relationship and the route — fully.
                    </p>
                </div>
            </section>

            <section className="relative py-20">
                <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="mono text-xs text-brand-blue uppercase mb-4">
                            / Our Approach
                        </p>
                        <h2 className="display text-3xl md:text-5xl text-[#1a1a1a]">
                            Human coordination.<br />Smart technology.
                        </h2>
                    </div>
                    <div className="space-y-6 text-gray-700 leading-relaxed">
                        <p>
                            We use internal tooling to match loads with the
                            right carriers in minutes — but our customers
                            never see a platform. They get one team, one
                            inbox, one accountable partner.
                        </p>
                        <p>
                            We believe the best logistics experience is the
                            one you don't have to manage. Our job is to make
                            transport feel invisible to your operation, while
                            being rigorously coordinated behind the scenes.
                        </p>
                    </div>
                </div>
            </section>

            <section className="relative py-20">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <p className="mono text-xs text-brand-green uppercase mb-4">
                        / Values
                    </p>
                    <h2 className="display text-3xl md:text-5xl text-[#1a1a1a] mb-12">
                        What we stand for.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VALUES.map((v, i) => (
                            <div
                                key={v.title}
                                data-testid={`value-${i}`}
                                className="brand-border-soft rounded-2xl p-8 hover:translate-y-[-4px] transition-all"
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-6">
                                    <v.icon
                                        size={20}
                                        className="text-brand-green"
                                    />
                                </div>
                                <h3 className="text-lg font-semibold text-[#1a1a1a] mb-3">
                                    {v.title}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {v.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative py-28">
                <div className="mx-auto max-w-4xl px-5 md:px-8 text-center">
                    <h2 className="display text-3xl md:text-5xl text-[#1a1a1a]">
                        Ready to simplify <br />
                        <span className="text-brand-green">your logistics?</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/request-transport"
                            className="btn-primary"
                            data-testid="about-cta-request"
                        >
                            Request Transport <ArrowUpRight size={16} />
                        </Link>
                        <Link to="/contact" className="btn-ghost">
                            Talk To Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
